<!-- frontend/src/views/admin/LocationManagementView.vue -->
<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="flex items-center justify-end">
          <!-- Actions -->
          <div class="flex space-x-3">
            <button
              @click="exportLocationReport"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download class="w-4 h-4 mr-2" />
              Export Report
            </button>
            <button
              @click="refreshData"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw :class="['w-4 h-4 mr-2', loading && 'animate-spin']" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Users class="h-8 w-8 text-green-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Active Tracking</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ locationStats.activeTracking }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Building class="h-8 w-8 text-blue-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">In Office</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ locationStats.inOffice }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <MapPin class="h-8 w-8 text-purple-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Field Work</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ locationStats.fieldWork }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <AlertTriangle class="h-8 w-8 text-red-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Issues</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ locationStats.issues }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Policy Settings -->
      <div class="bg-white rounded-lg shadow p-6 mb-8">
        <h3 class="text-lg font-medium text-gray-900 mb-4">
          Location Tracking Policy
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Mandatory Tracking -->
          <div class="border rounded-lg p-4">
            <h4 class="font-medium text-gray-900 mb-2">Mandatory Tracking</h4>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600"
                >Force location tracking for all employees</span
              >
              <div class="flex items-center">
                <input
                  id="mandatory-tracking"
                  v-model="policy.mandatoryTracking"
                  @change="updatePolicy"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  for="mandatory-tracking"
                  class="ml-2 text-sm text-green-600 font-medium"
                >
                  {{ policy.mandatoryTracking ? 'ENABLED' : 'DISABLED' }}
                </label>
              </div>
            </div>
          </div>

          <!-- High Accuracy Required -->
          <div class="border rounded-lg p-4">
            <h4 class="font-medium text-gray-900 mb-2">High Accuracy Mode</h4>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600"
                >Require GPS high accuracy</span
              >
              <div class="flex items-center">
                <input
                  id="high-accuracy"
                  v-model="policy.requireHighAccuracy"
                  @change="updatePolicy"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label for="high-accuracy" class="ml-2 text-sm text-gray-600">
                  {{ policy.requireHighAccuracy ? 'Required' : 'Optional' }}
                </label>
              </div>
            </div>
          </div>

          <!-- Update Frequency -->
          <div class="border rounded-lg p-4">
            <h4 class="font-medium text-gray-900 mb-2">Update Frequency</h4>
            <select
              v-model="policy.updateFrequency"
              @change="updatePolicy"
              class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
            >
              <option value="15000">Every 15 seconds</option>
              <option value="30000">Every 30 seconds</option>
              <option value="60000">Every minute</option>
              <option value="300000">Every 5 minutes</option>
            </select>
          </div>
        </div>

        <!-- Policy Status -->
        <div class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="flex items-center">
            <Shield class="w-5 h-5 text-blue-600 mr-3" />
            <div>
              <h5 class="text-sm font-medium text-blue-800">
                Current Policy Status
              </h5>
              <p class="text-sm text-blue-700 mt-1">
                Location tracking is
                {{ policy.mandatoryTracking ? 'MANDATORY' : 'OPTIONAL' }} for
                all employees.
                {{
                  policy.requireHighAccuracy
                    ? 'High accuracy GPS is required.'
                    : 'Standard accuracy is allowed.'
                }}
                Updates occur every
                {{ formatUpdateFrequency(policy.updateFrequency) }}.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Real-time Employee Locations -->
      <div class="bg-white rounded-lg shadow p-6 mb-8">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            Real-time Employee Locations
          </h3>
          <div class="flex items-center space-x-3">
            <!-- Filters -->
            <select
              v-model="locationFilter"
              @change="filterLocations"
              class="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Employees</option>
              <option value="office">In Office</option>
              <option value="field">Field Work</option>
              <option value="remote">Remote</option>
              <option value="offline">Offline</option>
            </select>

            <span class="text-xs text-gray-500">
              Last updated: {{ lastUpdated }}
            </span>
          </div>
        </div>

        <!-- Locations Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Employee
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Location Status
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Current Location
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Update
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="loading">
                <td colspan="5" class="px-6 py-12 text-center">
                  <RefreshCw
                    class="w-6 h-6 animate-spin mx-auto text-gray-400"
                  />
                  <p class="text-gray-500 mt-2">
                    Loading employee locations...
                  </p>
                </td>
              </tr>
              <tr v-else-if="filteredLocations.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                  No employees found matching your filter.
                </td>
              </tr>
              <tr
                v-else
                v-for="employee in filteredLocations"
                :key="employee.id"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <div
                        class="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center"
                      >
                        <User class="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ employee.name }}
                      </div>
                      <div class="text-sm text-gray-500">
                        {{ employee.email }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      getLocationStatusClass(employee.locationStatus),
                    ]"
                  >
                    {{ employee.locationStatus }}
                  </span>
                  <div v-if="employee.isOnline" class="flex items-center mt-1">
                    <div class="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    <span class="text-xs text-green-600">Online</span>
                  </div>
                  <div v-else class="flex items-center mt-1">
                    <div class="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                    <span class="text-xs text-gray-500">Offline</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ employee.currentLocation }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ employee.coordinates }}
                  </div>
                  <div v-if="employee.accuracy" class="text-xs text-gray-400">
                    Accuracy: {{ employee.accuracy }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ employee.lastUpdate }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex space-x-2">
                    <button
                      @click="viewEmployeeDetails(employee)"
                      class="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Eye class="w-4 h-4" />
                    </button>
                    <button
                      @click="viewLocationHistory(employee)"
                      class="text-green-600 hover:text-green-900"
                      title="Location History"
                    >
                      <MapPin class="w-4 h-4" />
                    </button>
                    <button
                      v-if="!employee.isOnline"
                      @click="sendLocationRequest(employee)"
                      class="text-orange-600 hover:text-orange-900"
                      title="Request Location Update"
                    >
                      <RefreshCw class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Office Locations Management -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            Office & Field Locations
          </h3>
          <button
            @click="openAddLocationModal"
            class="inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700"
          >
            <Plus class="w-4 h-4 mr-2" />
            Add Location
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="location in officeLocations"
            :key="location.id"
            class="border rounded-lg p-4"
          >
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-gray-900">{{ location.name }}</h4>
              <span
                :class="[
                  'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                  location.type === 'office'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800',
                ]"
              >
                {{ location.type }}
              </span>
            </div>
            <p class="text-sm text-gray-600 mb-2">{{ location.address }}</p>
            <div class="text-xs text-gray-500 mb-3">
              Radius: {{ location.radius }}m | Employees:
              {{ location.employeeCount }}
            </div>
            <div class="flex space-x-2">
              <button
                @click="editLocation(location)"
                class="text-blue-600 hover:text-blue-900 text-sm"
              >
                Edit
              </button>
              <button
                @click="deleteLocation(location)"
                class="text-red-600 hover:text-red-900 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Location Modal -->
    <div
      v-if="showAddLocationModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="showAddLocationModal = false"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">Add New Location</h3>
          <button
            @click="showAddLocationModal = false"
            class="text-gray-400 hover:text-gray-600"
          >
            <X class="w-6 h-6" />
          </button>
        </div>

        <!-- Add location form -->
        <form @submit.prevent="addLocation" class="space-y-4">
          <!-- Location Name -->
          <div>
            <label for="location-name" class="block text-sm font-medium text-gray-700 mb-1">
              Location Name *
            </label>
            <input
              id="location-name"
              v-model="locationForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': formErrors.name }"
              placeholder="Enter location name"
            />
            <p v-if="formErrors.name" class="mt-1 text-sm text-red-600">{{ formErrors.name }}</p>
          </div>

          <!-- Location Type -->
          <div>
            <label for="location-type" class="block text-sm font-medium text-gray-700 mb-1">
              Location Type
            </label>
            <select
              id="location-type"
              v-model="locationForm.type"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="office">Office</option>
              <option value="client_site">Client Site</option>
              <option value="warehouse">Warehouse</option>
              <option value="field">Field Location</option>
            </select>
          </div>

          <!-- Address -->
          <div>
            <label for="location-address" class="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id="location-address"
              v-model="locationForm.address"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter address"
            />
          </div>

          <!-- Coordinates -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                Coordinates *
              </label>
              <button
                type="button"
                @click="detectCurrentLocation"
                :disabled="detectingLocation"
                class="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
              >
                <Navigation class="w-3 h-3 mr-1" :class="{ 'animate-spin': detectingLocation }" />
                <span v-if="detectingLocation">Detecting...</span>
                <span v-else>Detect Current Location</span>
              </button>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="location-latitude" class="block text-sm font-medium text-gray-500 mb-1">
                  Latitude
                </label>
                <input
                  id="location-latitude"
                  v-model="locationForm.latitude"
                  type="number"
                  step="any"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  :class="{ 'border-red-500': formErrors.latitude }"
                  placeholder="40.7128"
                />
                <p v-if="formErrors.latitude" class="mt-1 text-sm text-red-600">{{ formErrors.latitude }}</p>
              </div>
              <div>
                <label for="location-longitude" class="block text-sm font-medium text-gray-500 mb-1">
                  Longitude
                </label>
                <input
                  id="location-longitude"
                  v-model="locationForm.longitude"
                  type="number"
                  step="any"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  :class="{ 'border-red-500': formErrors.longitude }"
                  placeholder="-74.0060"
                />
                <p v-if="formErrors.longitude" class="mt-1 text-sm text-red-600">{{ formErrors.longitude }}</p>
              </div>
            </div>
          </div>

          <!-- Radius -->
          <div>
            <label for="location-radius" class="block text-sm font-medium text-gray-700 mb-1">
              Radius (meters) *
            </label>
            <input
              id="location-radius"
              v-model="locationForm.radius"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': formErrors.radius }"
              placeholder="100"
            />
            <p v-if="formErrors.radius" class="mt-1 text-sm text-red-600">{{ formErrors.radius }}</p>
          </div>

          <!-- Description -->
          <div>
            <label for="location-description" class="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="location-description"
              v-model="locationForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter location description"
            ></textarea>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              @click="showAddLocationModal = false"
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
            >
              <span v-if="loading">Adding...</span>
              <span v-else>Add Location</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Location Modal -->
    <div
      v-if="showEditLocationModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="showEditLocationModal = false"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">Edit Location</h3>
          <button
            @click="showEditLocationModal = false"
            class="text-gray-400 hover:text-gray-600"
          >
            <X class="w-6 h-6" />
          </button>
        </div>

        <!-- Edit location form -->
        <form @submit.prevent="updateLocation" class="space-y-4">
          <!-- Location Name -->
          <div>
            <label for="edit-location-name" class="block text-sm font-medium text-gray-700 mb-1">
              Location Name *
            </label>
            <input
              id="edit-location-name"
              v-model="locationForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': formErrors.name }"
              placeholder="Enter location name"
            />
            <p v-if="formErrors.name" class="mt-1 text-sm text-red-600">{{ formErrors.name }}</p>
          </div>

          <!-- Location Type -->
          <div>
            <label for="edit-location-type" class="block text-sm font-medium text-gray-700 mb-1">
              Location Type
            </label>
            <select
              id="edit-location-type"
              v-model="locationForm.type"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="office">Office</option>
              <option value="client_site">Client Site</option>
              <option value="warehouse">Warehouse</option>
              <option value="field">Field Location</option>
            </select>
          </div>

          <!-- Address -->
          <div>
            <label for="edit-location-address" class="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id="edit-location-address"
              v-model="locationForm.address"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter address"
            />
          </div>

          <!-- Coordinates -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                Coordinates *
              </label>
              <button
                type="button"
                @click="detectCurrentLocation"
                :disabled="detectingLocation"
                class="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
              >
                <Navigation class="w-3 h-3 mr-1" :class="{ 'animate-spin': detectingLocation }" />
                <span v-if="detectingLocation">Detecting...</span>
                <span v-else>Detect Current Location</span>
              </button>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="edit-location-latitude" class="block text-sm font-medium text-gray-500 mb-1">
                  Latitude
                </label>
                <input
                  id="edit-location-latitude"
                  v-model="locationForm.latitude"
                  type="number"
                  step="any"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  :class="{ 'border-red-500': formErrors.latitude }"
                  placeholder="40.7128"
                />
                <p v-if="formErrors.latitude" class="mt-1 text-sm text-red-600">{{ formErrors.latitude }}</p>
              </div>
              <div>
                <label for="edit-location-longitude" class="block text-sm font-medium text-gray-500 mb-1">
                  Longitude
                </label>
                <input
                  id="edit-location-longitude"
                  v-model="locationForm.longitude"
                  type="number"
                  step="any"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  :class="{ 'border-red-500': formErrors.longitude }"
                  placeholder="-74.0060"
                />
                <p v-if="formErrors.longitude" class="mt-1 text-sm text-red-600">{{ formErrors.longitude }}</p>
              </div>
            </div>
          </div>

          <!-- Radius -->
          <div>
            <label for="edit-location-radius" class="block text-sm font-medium text-gray-700 mb-1">
              Radius (meters) *
            </label>
            <input
              id="edit-location-radius"
              v-model="locationForm.radius"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': formErrors.radius }"
              placeholder="100"
            />
            <p v-if="formErrors.radius" class="mt-1 text-sm text-red-600">{{ formErrors.radius }}</p>
          </div>

          <!-- Description -->
          <div>
            <label for="edit-location-description" class="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="edit-location-description"
              v-model="locationForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter location description"
            ></textarea>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              @click="showEditLocationModal = false"
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
            >
              <span v-if="loading">Updating...</span>
              <span v-else>Update Location</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  Users,
  Building,
  MapPin,
  AlertTriangle,
  RefreshCw,
  Download,
  Eye,
  User,
  Plus,
  X,
  Shield,
  Navigation,
} from 'lucide-vue-next';

// Import services
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';

// State
const loading = ref(false);
const lastUpdated = ref('');
const locationFilter = ref('all');
const showAddLocationModal = ref(false);
const showEditLocationModal = ref(false);

// Form data for adding/editing locations
const locationForm = ref({
  id: null,
  name: '',
  type: 'office',
  latitude: '',
  longitude: '',
  radius: 100,
  address: '',
  description: ''
});

// Form validation
const formErrors = ref({});

// Geolocation state
const detectingLocation = ref(false);

// Policy settings
const policy = ref({
  mandatoryTracking: true,
  requireHighAccuracy: true,
  updateFrequency: 30000,
});

// Data arrays
const locationStats = ref({
  activeTracking: 0,
  inOffice: 0,
  fieldWork: 0,
  issues: 0,
});

const employeeLocations = ref([]);
const officeLocations = ref([]);

// Computed
const filteredLocations = computed(() => {
  if (locationFilter.value === 'all') return employeeLocations.value;

  return employeeLocations.value.filter(emp => {
    switch (locationFilter.value) {
      case 'office':
        return emp.locationStatus === 'In Office';
      case 'field':
        return emp.locationStatus === 'Field Work';
      case 'remote':
        return emp.locationStatus === 'Remote';
      case 'offline':
        return !emp.isOnline;
      default:
        return true;
    }
  });
});

// Methods
const getLocationStatusClass = status => {
  switch (status) {
    case 'In Office':
      return 'bg-blue-100 text-blue-800';
    case 'Field Work':
      return 'bg-green-100 text-green-800';
    case 'Remote':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatUpdateFrequency = freq => {
  const seconds = freq / 1000;
  if (seconds < 60) return `${seconds} seconds`;
  return `${seconds / 60} minute${seconds / 60 !== 1 ? 's' : ''}`;
};

const loadLocationData = async () => {
  try {
    loading.value = true;
    console.log('📍 Loading location data...');
    
    // Get live attendance data which includes location information
    const response = await attendanceService.admin.getLiveAttendance();
    
    if (response.data) {
      // Transform employee data
      employeeLocations.value = (response.data.employees || []).map(emp => ({
        id: emp.id,
        name: emp.name,
        email: emp.email,
        locationStatus: emp.mode === 'office' ? 'In Office' : 
                      emp.mode === 'field' ? 'Field Work' : 'Remote',
        currentLocation: emp.address || 'Unknown',
        coordinates: emp.latitude && emp.longitude ? 
                    `${emp.latitude}, ${emp.longitude}` : 'N/A',
        accuracy: emp.accuracy ? `${emp.accuracy}m` : 'N/A',
        lastUpdate: emp.lastUpdate || 'N/A',
        isOnline: emp.status === 'active',
      }));
      
      // Calculate stats
      const stats = response.data;
      locationStats.value = {
        activeTracking: stats.activeSessions?.length || 0,
        inOffice: stats.inOffice || 0,
        fieldWork: stats.fieldWork || 0,
        issues: stats.lateArrivals?.length || 0,
      };
    }
    
    lastUpdated.value = new Date().toLocaleTimeString();
  } catch (error) {
    console.error('❌ Error loading location data:', error);
    // Keep existing data or show empty state
  } finally {
    loading.value = false;
  }
};

const loadFieldLocations = async () => {
  try {
    const response = await attendanceService.getFieldLocations();
    console.log('📍 Field locations response:', response.data);
    
    if (response.data?.data?.locations) {
      officeLocations.value = response.data.data.locations.map(location => ({
        id: location.id,
        name: location.name,
        type: location.type || 'field',
        address: location.address,
        radius: location.radius || 100,
        employeeCount: location.employeeCount || 0,
      }));
    } else if (response.data && Array.isArray(response.data)) {
      // Fallback if data is directly an array
      officeLocations.value = response.data.map(location => ({
        id: location.id,
        name: location.name,
        type: location.type || 'field',
        address: location.address,
        radius: location.radius || 100,
        employeeCount: location.employeeCount || 0,
      }));
    }
  } catch (error) {
    console.error('❌ Error loading field locations:', error);
  }
};

const updatePolicy = async () => {
  try {
    console.log('🔄 Updating location policy:', policy.value);
    // This would need a dedicated endpoint for location policies
    // await adminService.updateLocationPolicy(policy.value);
    console.log('✅ Location policy updated');
  } catch (error) {
    console.error('❌ Error updating location policy:', error);
  }
};

const refreshData = async () => {
  await Promise.all([
    loadLocationData(),
    loadFieldLocations(),
  ]);
};

const exportLocationReport = async () => {
  try {
    console.log('📤 Exporting location report...');
    
    // Use attendance export with location data
    const response = await attendanceService.admin.exportAttendanceData({
      includeLocation: true,
      format: 'xlsx',
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `location-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Location report exported');
  } catch (error) {
    console.error('❌ Error exporting location report:', error);
  }
};

const filterLocations = () => {
  console.log('Filtering by:', locationFilter.value);
};

const viewEmployeeDetails = employee => {
  console.log('View details for:', employee.name);
};

const viewLocationHistory = employee => {
  console.log('View location history for:', employee.name);
};

const sendLocationRequest = employee => {
  console.log('Request location update for:', employee.name);
};

// Add location functionality
const openAddLocationModal = () => {
  resetLocationForm();
  showAddLocationModal.value = true;
};

const resetLocationForm = () => {
  locationForm.value = {
    id: null,
    name: '',
    type: 'office',
    latitude: '',
    longitude: '',
    radius: 100,
    address: '',
    description: ''
  };
  formErrors.value = {};
};

// Geolocation functionality
const detectCurrentLocation = async () => {
  if (!navigator.geolocation) {
    alert('Geolocation is not supported by this browser.');
    return;
  }

  detectingLocation.value = true;

  try {
    const position = await getCurrentPosition();
    locationForm.value.latitude = position.coords.latitude.toFixed(6);
    locationForm.value.longitude = position.coords.longitude.toFixed(6);
    
    console.log('✅ Current location detected:', {
      latitude: locationForm.value.latitude,
      longitude: locationForm.value.longitude,
      accuracy: position.coords.accuracy
    });

    // Try to get address from reverse geocoding (if available)
    try {
      await reverseGeocode(position.coords.latitude, position.coords.longitude);
    } catch (geoError) {
      console.warn('Could not get address from coordinates:', geoError);
    }

    // Clear any previous coordinate errors since we have valid coordinates now
    if (formErrors.value.latitude) delete formErrors.value.latitude;
    if (formErrors.value.longitude) delete formErrors.value.longitude;

  } catch (error) {
    console.error('❌ Error detecting location:', error);
    
    let errorMessage = 'Could not detect location. ';
    switch (error.code) {
      case 1: // PERMISSION_DENIED
        errorMessage += 'Location access was denied. Please enable location permissions and try again.';
        break;
      case 2: // POSITION_UNAVAILABLE
        errorMessage += 'Location information is unavailable.';
        break;
      case 3: // TIMEOUT
        errorMessage += 'Location request timed out.';
        break;
      default:
        errorMessage += 'An unknown error occurred.';
    }
    
    alert(errorMessage);
  } finally {
    detectingLocation.value = false;
  }
};

// Helper function to promisify geolocation
const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      {
        enableHighAccuracy: true,
        timeout: 15000, // 15 seconds
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Optional: Reverse geocoding to get address from coordinates
const reverseGeocode = async (latitude, longitude) => {
  // Skip reverse geocoding due to CORS restrictions in development
  // In production, this would be handled by the backend to avoid CORS issues
  try {
    // For now, just set a placeholder address
    locationForm.value.address = `Location at ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    console.log('📍 Location coordinates captured, address auto-filled with coordinates');
    
    // TODO: In production, this should call the backend API:
    // const response = await attendanceService.reverseGeocode(latitude, longitude);
    // locationForm.value.address = response.data.address;
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
  }
};

const validateLocationForm = () => {
  const errors = {};
  
  if (!locationForm.value.name.trim()) {
    errors.name = 'Location name is required';
  }
  
  if (!locationForm.value.latitude || isNaN(parseFloat(locationForm.value.latitude))) {
    errors.latitude = 'Valid latitude is required';
  }
  
  if (!locationForm.value.longitude || isNaN(parseFloat(locationForm.value.longitude))) {
    errors.longitude = 'Valid longitude is required';
  }
  
  if (!locationForm.value.radius || isNaN(parseInt(locationForm.value.radius)) || locationForm.value.radius <= 0) {
    errors.radius = 'Valid radius is required';
  }
  
  formErrors.value = errors;
  return Object.keys(errors).length === 0;
};

const addLocation = async () => {
  if (!validateLocationForm()) {
    return;
  }

  try {
    loading.value = true;
    const locationData = {
      name: locationForm.value.name.trim(),
      type: locationForm.value.type,
      latitude: parseFloat(locationForm.value.latitude),
      longitude: parseFloat(locationForm.value.longitude),
      radius: parseInt(locationForm.value.radius),
      address: locationForm.value.address.trim(),
      description: locationForm.value.description.trim()
    };

    const response = await attendanceService.createFieldLocation(locationData);
    
    if (response.data.success) {
      console.log('✅ Location added successfully');
      showAddLocationModal.value = false;
      resetLocationForm();
      await refreshData(); // Reload the locations list
    }
  } catch (error) {
    console.error('❌ Error adding location:', error);
    // You might want to show a toast notification here
  } finally {
    loading.value = false;
  }
};

// Edit location functionality
const editLocation = (location) => {
  locationForm.value = {
    id: location.id,
    name: location.name,
    type: location.type,
    latitude: location.latitude?.toString() || '',
    longitude: location.longitude?.toString() || '',
    radius: location.radius || 100,
    address: location.address || '',
    description: location.description || ''
  };
  formErrors.value = {};
  showEditLocationModal.value = true;
};

const updateLocation = async () => {
  if (!validateLocationForm()) {
    return;
  }

  try {
    loading.value = true;
    const locationData = {
      name: locationForm.value.name.trim(),
      type: locationForm.value.type,
      latitude: parseFloat(locationForm.value.latitude),
      longitude: parseFloat(locationForm.value.longitude),
      radius: parseInt(locationForm.value.radius),
      address: locationForm.value.address.trim(),
      description: locationForm.value.description.trim()
    };

    const response = await attendanceService.updateFieldLocation(locationForm.value.id, locationData);
    
    if (response.data.success) {
      console.log('✅ Location updated successfully');
      showEditLocationModal.value = false;
      resetLocationForm();
      await refreshData(); // Reload the locations list
    }
  } catch (error) {
    console.error('❌ Error updating location:', error);
  } finally {
    loading.value = false;
  }
};

// Delete location functionality
const deleteLocation = async (location) => {
  if (!confirm(`Are you sure you want to delete "${location.name}"? This action cannot be undone.`)) {
    return;
  }

  try {
    loading.value = true;
    const response = await attendanceService.deleteFieldLocation(location.id);
    
    if (response.data.success) {
      console.log('✅ Location deleted successfully');
      await refreshData(); // Reload the locations list
    }
  } catch (error) {
    console.error('❌ Error deleting location:', error);
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  refreshData();
});
</script>

<style scoped>
/* Add any custom styles here */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
